import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { IFinancialCategoryDataSummaryModel } from "../../types/models";
import { getShortMonthName } from "../../utils/common";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { FinancialCategoryEnumModel } from "../../types/types";

interface FinancialLineChartWrapperProps {
  category: FinancialCategoryEnumModel;
  selectedYearIds?: number[];
  yearText?: string | number;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  isPercentageCategory,
  decimalPlaces,
}: any) => {
  const brand = useTheme();

  if (active && payload && payload.length) {
    const value = payload[0].value;
    const formattedValue = !isPercentageCategory
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value)
      : `${Number(value).toFixed(decimalPlaces)}%`;

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>{label}</p>
        <p style={{ margin: "4px 0 0 0", color: brand.palette.primary.dark }}>
          {formattedValue}
        </p>
      </div>
    );
  }
  return null;
};

export const FinancialLineChartWrapper: React.FC<
  FinancialLineChartWrapperProps
> = ({ category, selectedYearIds }) => {
  const [timePeriod, setTimePeriod] = useState<"quarter" | "month">("quarter");

  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { financialCategoryState } = useSelector(
    (state: RootState) => state.analytics
  );

  const categoryData = financialCategoryState.data;

  // Helper function to determine appropriate decimal places for percentage display
  const getDecimalPlaces = (data: Array<{ amount?: number }>) => {
    const amounts =
      (data
        ?.map((d) => d.amount)
        .filter((val) => val !== undefined) as number[]) || [];
    if (amounts.length === 0) return 2;

    const maxDataValue = Math.max(...amounts);
    const minDataValue = Math.min(...amounts);
    const range = maxDataValue - minDataValue;

    if (range < 0.01) return 4;
    if (range < 0.1) return 3;
    return 2;
  };

  // For multiple years or no years selected, we show yearly data
  // For single year selected, we show quarterly/monthly data within that year
  const isSingleYearView = selectedYearIds && selectedYearIds.length === 1;
  const selectedYearId = isSingleYearView ? selectedYearIds[0] : undefined;

  // Generate complete set of periods for selected year
  // const allPeriods = selectedYearId
  //   ? Array.from({ length: timePeriod === "month" ? 12 : 4 }, (_, i) => i + 1)
  //   : years.map((year) => year.id);

  const allPeriods = isSingleYearView
    ? Array.from({ length: timePeriod === "month" ? 12 : 4 }, (_, i) => i + 1)
    : (selectedYearIds ?? []).length > 0
    ? selectedYearIds!
    : years.map((year) => year.id);

  const groupedData = selectedYearId
    ? categoryData?.reduce((acc, item) => {
        const groupKey = timePeriod === "quarter" ? item.quarter : item.month;
        const existing = acc.find((i) => i[timePeriod] === groupKey);

        if (existing) {
          existing.totalCount =
            (existing.totalCount || 0) + (item.totalCount || 0);
          existing.totalAmount =
            (existing.totalAmount || 0) + (item.totalAmount || 0);
        } else {
          acc.push({
            ...item,
            totalCount: item.totalCount || 0,
            totalAmount: item.totalAmount || 0,
            [timePeriod]: groupKey,
          });
        }
        return acc;
      }, [] as IFinancialCategoryDataSummaryModel[])
    : categoryData;

  // Merge with empty periods if year is selected
  const mergedData = selectedYearId
    ? allPeriods.map((period) => {
        const existing = groupedData?.find((d) => d[timePeriod] === period);
        return (
          existing || {
            [timePeriod]: period,
            totalCount: 0,
            totalAmount: 0,
          }
        );
      })
    : allPeriods.map((period) => {
        const existing = groupedData?.find((d) => d.yearId === period);
        return (
          existing || {
            yearId: period,
            totalCount: 0,
            totalAmount: 0,
          }
        );
      });

  const trendData = mergedData
    ?.slice() // Create copy to avoid mutating original array
    ?.map((item) => {
      const year = !selectedYearId
        ? years.find((y) => y.id === item.yearId)?.year
        : undefined;
      const count = item.totalCount;
      const amount = item.totalAmount;
      const quarter = timePeriod === "quarter" ? `Q${item.quarter}` : undefined;
      const month = timePeriod === "month" ? item.month : undefined;
      return { year, count, amount, quarter, month };
    })
    ?.sort((a, b) => {
      if (selectedYearId === undefined) {
        return (a.year || 0) - (b.year || 0);
      }
      return 0;
    });

  // Determine if this category should show amounts (financial values) or counts
  const isPercentageCategory =
    category === FinancialCategoryEnumModel.Commission ||
    category === FinancialCategoryEnumModel.NetIncomePercent;
  const showAmount = true; // Always show amount since all data (including percentages) are stored in totalAmount

  return (
    <div style={{ position: "relative" }}>
      {selectedYearId && (
        <Box sx={{ position: "absolute", right: 30, top: -40, zIndex: 1 }}>
          <Select
            value={timePeriod}
            onChange={(e) =>
              setTimePeriod(e.target.value as "quarter" | "month")
            }
            size="small"
          >
            <MenuItem value="quarter">Quarterly View</MenuItem>
            <MenuItem value="month">Monthly View</MenuItem>
          </Select>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={trendData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={selectedYearId ? timePeriod : "year"}
            tickFormatter={(value) => {
              if (!selectedYearId) return `${value}`;
              return timePeriod === "month"
                ? getShortMonthName(value)
                : `${value}`;
            }}
          />
          <YAxis
            allowDecimals={true}
            domain={[0, "auto"]}
            tickCount={isPercentageCategory ? 6 : undefined}
            tickFormatter={(value) => {
              if (!isPercentageCategory) {
                // Format large numbers with abbreviations
                const absValue = Math.abs(value);
                if (absValue >= 1000000) {
                  return `$${(value / 1000000).toFixed(1)}M`;
                } else if (absValue >= 1000) {
                  return `$${(value / 1000).toFixed(1)}K`;
                } else {
                  return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value);
                }
              } else {
                // For percentages, determine appropriate decimal places based on data range
                const decimalPlaces = getDecimalPlaces(trendData || []);
                return `${Number(value).toFixed(decimalPlaces)}%`;
              }
            }}
          />
          <Tooltip
            content={
              <CustomTooltip
                isPercentageCategory={isPercentageCategory}
                decimalPlaces={getDecimalPlaces(trendData || [])}
              />
            }
            labelFormatter={(label) => {
              // Format the tooltip label (x-axis value)
              if (
                selectedYearId &&
                timePeriod === "month" &&
                typeof label === "number"
              ) {
                return getShortMonthName(label);
              }
              return label;
            }}
          />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingRight: 0, paddingTop: 0, paddingBottom: 5 }}
          />

          {showAmount ? (
            <Line
              type="monotone"
              dataKey="amount"
              stroke="#1976d2"
              strokeWidth={2}
              activeDot={{ r: 8 }}
              name="Volume"
              legendType="none"
            />
          ) : (
            <Line
              type="monotone"
              dataKey="count"
              stroke="#1976d2"
              strokeWidth={2}
              activeDot={{ r: 8 }}
              name="Percentage"
              legendType="none"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
